import { useQueries } from '@tanstack/react-query';
import { getIsAuthenticated } from 'features/customer/store/selectors';
import {
  getQueryKey as getWorkspaceInfoQueryKey,
  invalidateWorkspaceInfoQuery,
} from 'features/workspaces/useGetWorkspaceInfo';
import {
  httpCustomGetWorkspaceInfo,
  WorkspaceInfo,
} from 'services/backofficeIntegration/http/endpoints/workspace/httpCustomGetWorkspaceInfo';
import { useGetAllCustomerWorkspaceIds } from 'services/backofficeIntegration/http/endpoints/workspace/useGetAllCustomerWorkspaceIds';
import { useAppSelector } from 'store/hooks';
import { assertNonNullable } from 'utils/typescript/nonNullable';

export const useGetAllCustomerWorkspacesInfo = () => {
  const isAuthenticated = useAppSelector(getIsAuthenticated);
  const { data: workspaceIds = [] } = useGetAllCustomerWorkspaceIds();

  const queries = useQueries({
    queries: workspaceIds.map(workspaceId => ({
      enabled: isAuthenticated && !!workspaceId,
      queryKey: getWorkspaceInfoQueryKey(workspaceId),
      queryFn: () => {
        assertNonNullable(workspaceId);
        return httpCustomGetWorkspaceInfo.callEndpoint({ workspaceId });
      },
      // Cache for 1 hour
      staleTime: 3_600_000,
      // Don't throw errors, we want to handle them gracefully
      throwOnError: false,
    })),
  });

  // Map results back to maintain order and handle failures
  const data = workspaceIds.map((_, index) => {
    const query = queries[index];
    if (query.isSuccess && query.data) {
      return query.data;
    }
    return null;
  });

  const dataFiltered = data.filter(Boolean) as WorkspaceInfo[];

  // Aggregate loading and error states
  const isLoading = queries.some(query => query.isLoading);
  const isError = queries.some(query => query.isError);
  const isFetching = queries.some(query => query.isFetching);

  const errors = queries.map(query => query.error).filter(Boolean);

  return {
    data,
    dataFiltered,
    isLoading,
    isError,
    isFetching,
    errors,
    queries,
  };
};

export const invalidateGetAllCustomerWorkspaceIdsQuery = () => invalidateWorkspaceInfoQuery();
